import { useRef } from 'react';

import { constants as appClientConstants } from '@edf-pkg/app-client';

import { UserSignActions } from '$app-web/constants';
import useErrorHandler from '$app-web/hooks/use-error-handler';
import { getCurrentTimezone, getIdOfRole, isResearcher } from '$app-web/utils';

import useSignIn from './use-sign-in';
import useSignUpEndpoint from './use-sign-up.endpoint';

const useSignUp = ({ onSuccess } = {}) => {
    const userPassword = useRef(null);

    const { handleError } = useErrorHandler();

    const { signIn, isLoading: isSignInLoading } = useSignIn({ actionSource: UserSignActions.SignUp });

    const {
        mutate,
        isLoading: isSignUpLoading,
        isError,
        isSuccess,
        ...rest
    } = useSignUpEndpoint({
        onSuccess: (result) => {
            signIn({ username: result.user.username, password: userPassword.current });
            onSuccess?.(result);
        },
        onError: (error) => handleError(error),
    });

    const signUp = (signUpData, formikActions) => {
        formikActions?.setSubmitting(true);

        const { email, firstName, lastName, password, role, organization } = signUpData;

        userPassword.current = password;

        const olsonTimezone = getCurrentTimezone();

        const headers = {
            ...(olsonTimezone && { 'X-Avc-Timezone': olsonTimezone }),
        };

        const payload = {
            client_id: appClientConstants.AUTHENTICATION_CLIENT_IDS.WEB,
            email,
            password,
            first_name: firstName,
            last_name: lastName,
            role_id: getIdOfRole(role),
        };

        if (isResearcher(role)) {
            payload.org_name = organization;
        }

        mutate(
            {
                ...payload,
                extraAxiosConfig: {
                    headers,
                },
            },
            {
                onSettled: () => {
                    formikActions?.setSubmitting(false);
                },
            }
        );
    };

    return {
        signUp,
        isLoading: isSignUpLoading || isSignInLoading,
        isSuccess,
        isError,
        ...rest,
    };
};

export default useSignUp;
